import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search } from "lucide-react";
import Card from "./Card";

const fmtDate = (iso, lang) => (iso ? new Date(iso).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const norm = (s) => (s || "").toLowerCase();

function StatusBadge({ p, it }) {
  const [label, cls] = p.suspended
    ? [it ? "Sospeso" : "Suspended", "bg-[#fdecec] text-[#b42318]"]
    : !p.password_set
      ? [it ? "In attesa di password" : "Awaiting password", "bg-[#fff4e5] text-[#b45309]"]
      : [it ? "Attivo" : "Active", "bg-[#e8f5ec] text-[#15803d]"];
  return <span className={`text-[11px] font-medium px-2.5 py-1 rounded-full whitespace-nowrap ${cls}`} data-testid={`pro-status-${p.id}`}>{label}</span>;
}

function ProRow({ p, it, lang, busy, onToggle }) {
  return (
    <div className={`border border-[#e4e4e7] rounded-xl p-4 md:p-5 transition-colors ${p.suspended ? "bg-[#fafafa]" : "bg-white"}`} data-testid={`pro-${p.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className={`text-[15px] font-semibold ${p.suspended ? "text-[#6e6e73]" : "text-[#1d1d1f]"}`}>{p.company || "—"}</div>
          <div className="mt-0.5 text-[12px] text-[#6e6e73]">{p.partita_iva ? `P.IVA ${p.partita_iva}` : (it ? "P.IVA non registrata" : "No VAT number")}</div>
        </div>
        <StatusBadge p={p} it={it} />
      </div>
      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-[13px] text-[#1d1d1f]">
        <div><span className="text-[#6e6e73]">{it ? "Referente" : "Contact"}:</span> {p.name}</div>
        <div className="break-all"><span className="text-[#6e6e73]">Email:</span> <a href={`mailto:${p.email}`} className="text-[#1D3F94] hover:underline">{p.email}</a></div>
        {p.telefono && <div><span className="text-[#6e6e73]">{it ? "Telefono" : "Phone"}:</span> <a href={`tel:${p.telefono.replace(/\s/g, "")}`} className="text-[#1D3F94] hover:underline">{p.telefono}</a></div>}
        <div><span className="text-[#6e6e73]">{it ? "Approvato il" : "Approved"}:</span> {fmtDate(p.created_at, lang)} · <span className="text-[#6e6e73]">{it ? "Ultimo accesso" : "Last sign-in"}:</span> {fmtDate(p.last_login_at, lang)}</div>
      </div>
      <div className="mt-4">
        <button
          type="button"
          disabled={busy}
          onClick={() => onToggle(p)}
          className={p.suspended ? "btn-pill btn-primary text-[13px] disabled:opacity-60" : "text-[13px] text-[#6e6e73] hover:text-[#b42318] transition-colors disabled:opacity-60"}
          data-testid={`pro-toggle-${p.id}`}
        >
          {p.suspended ? (it ? "Riattiva accesso" : "Reactivate access") : (it ? "Sospendi accesso" : "Suspend access")}
        </button>
      </div>
    </div>
  );
}

export default function AdminProfessionals({ lang, authAxios }) {
  const it = lang === "it";
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    authAxios().get("/admin/professionals")
      .then((r) => setItems(r.data))
      .catch((err) => console.error("[portal] professionals load failed:", err));
  }, [authAxios]);

  const shown = useMemo(() => {
    const n = norm(q.trim());
    return n ? items.filter((p) => norm(`${p.company} ${p.name} ${p.email} ${p.partita_iva}`).includes(n)) : items;
  }, [items, q]);

  const toggle = async (p) => {
    const msg = p.suspended
      ? (it ? `Riattivare l'accesso di ${p.company}?` : `Reactivate ${p.company}?`)
      : (it ? `Sospendere l'accesso di ${p.company}? Non potrà più entrare finché non lo riattivi.` : `Suspend ${p.company}?`);
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const { data } = await authAxios().post(`/admin/professionals/${p.id}/${p.suspended ? "reactivate" : "suspend"}`);
      setItems((cur) => cur.map((x) => (x.id === p.id ? { ...x, suspended: data.suspended } : x)));
      toast.success(data.suspended ? (it ? "Accesso sospeso" : "Access suspended") : (it ? "Accesso riattivato" : "Access reactivated"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  const active = items.filter((p) => !p.suspended).length;

  return (
    <div className="mb-6" data-testid="admin-professionals">
      <Card
        id="admin-professionals"
        title={it ? "Professionisti" : "Professionals"}
        right={<span className="text-[12px] text-[#6e6e73]" data-testid="pro-count">{active} {it ? "attivi" : "active"} · {items.length - active} {it ? "sospesi" : "suspended"}</span>}
      >
        {items.length > 3 && (
          <label className="relative block mb-4">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#6e6e73]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={it ? "Cerca per azienda, nome, email o P.IVA" : "Search"} className="w-full bg-[#f5f5f7] rounded-full pl-10 pr-4 py-2.5 text-[14px] outline-none focus:ring-2 focus:ring-[#1D3F94]/30" data-testid="pro-search" />
          </label>
        )}
        {shown.length === 0 ? (
          <div className="py-8 text-center text-[13px] text-[#6e6e73]" data-testid="pro-empty">{it ? "Nessun professionista approvato." : "No approved professionals."}</div>
        ) : (
          <div className="space-y-3 max-h-[640px] overflow-y-auto pr-1" data-lenis-prevent>
            {shown.map((p) => <ProRow key={p.id} p={p} it={it} lang={lang} busy={busy} onToggle={toggle} />)}
          </div>
        )}
      </Card>
    </div>
  );
}
